import React from "react";
import { Link } from "react-router-dom";
import { Box, Collapse, InputBase, Typography } from "@mui/material";
import SearchRounded from "@mui/icons-material/SearchRounded";
import { motion } from "framer-motion";
import { colors } from "../colors";
import { PageContainer } from "../PageContainer";

const MotionBox = motion(Box);

const HERO_BG = "#080808";
const TOTAL_QUESTIONS = 12;

const heroSectionSx = {
  position: "relative",
  overflow: "hidden",
  backgroundColor: HERO_BG,
  px: { xs: 2, md: 4 },
  minHeight: "100svh",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end",
  pb: { xs: "80px", md: "96px" },
};

const REVEAL = {
  initial: { opacity: 0, y: 28, filter: "blur(10px)" },
  whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
  viewport: { once: true, amount: 0.25 },
};

const REVEAL_TRANSITION = { duration: 0.9, ease: [0.22, 1, 0.36, 1] };

const HERO_ENTER = {
  initial: { y: 26, opacity: 0, filter: "blur(10px)" },
  animate: { y: 0, opacity: 1, filter: "blur(0px)" },
};

const sectionSx = {
  py: { xs: "80px", md: "120px" },
  px: { xs: 2, md: 4 },
  borderTop: `1px solid ${colors.border.subtle}`,
  backgroundColor: HERO_BG,
};

const titleSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: { xs: "2.5rem", sm: "3.25rem", md: "3.75rem" },
  fontWeight: 700,
  letterSpacing: "-0.04em",
  lineHeight: 0.95,
  textTransform: "uppercase",
  color: colors.text.primary,
  mb: 2,
};

const btnPrimarySx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: colors.text.contrast,
  background: colors.primary.main,
  px: "32px",
  py: "14px",
  textDecoration: "none",
  clipPath: "polygon(10px 0%, 100% 0%, calc(100% - 10px) 100%, 0% 100%)",
  border: "1px solid transparent",
  position: "relative",
  overflow: "hidden",
  display: "inline-block",
  transition:
    "transform .25s cubic-bezier(.22,1,.36,1), box-shadow .25s ease, border-color .25s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)",
    transform: "translateX(-120%)",
    transition: "transform .6s ease",
  },
  "&:hover": {
    transform: "translateY(-2px)",
    borderColor: "rgba(255,255,255,0.18)",
    boxShadow: "0 10px 30px rgba(232,163,61,0.18)",
  },
  "&:hover::before": { transform: "translateX(120%)" },
  "&:active": { transform: "translateY(0px)" },
};

const btnGhostSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: colors.text.secondary,
  px: "32px",
  py: "13px",
  textDecoration: "none",
  border: "1px solid rgba(255,255,255,0.08)",
  background: "linear-gradient(180deg, rgba(255,255,255,0.01), transparent)",
  position: "relative",
  overflow: "hidden",
  display: "inline-block",
  transition:
    "transform .25s cubic-bezier(.22,1,.36,1), border-color .25s ease, color .25s ease, box-shadow .25s ease, background .25s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "15%",
    right: "15%",
    height: "1px",
    background: "linear-gradient(to right, transparent, rgba(255,255,255,0.18), transparent)",
    opacity: 0,
    transition: "opacity .25s ease",
  },
  "&:hover": {
    color: colors.text.primary,
    borderColor: "rgba(232,163,61,0.25)",
    background: "linear-gradient(180deg, rgba(232,163,61,0.04), rgba(232,163,61,0.02))",
    boxShadow: "0 8px 24px rgba(232,163,61,0.08)",
    transform: "translateY(-2px)",
  },
  "&:hover::before": { opacity: 1 },
  "&:active": { transform: "translateY(0px)" },
};

const GOLD_GRADIENT = "linear-gradient(180deg, #f7cb79 0%, #f0b458 40%, #de8f2c 100%)";

const NAV_CATEGORIES = [
  { id: "all", label: "All questions", count: TOTAL_QUESTIONS },
  { id: "platform", label: "Platform status", count: 2 },
  { id: "account", label: "Account & access", count: 3 },
  { id: "competitive", label: "Competitive features", count: 3 },
  { id: "membership", label: "Memberships", count: 2 },
  { id: "anticheat", label: "Anti-cheat", count: 2 },
];

const FAQ_CATEGORIES = [
  {
    id: "platform",
    name: "Platform status",
    icon: (
      <>
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </>
    ),
    items: [
      {
        id: "platform-live",
        searchKeywords: "is ouro fully live yet early platform status launch",
        question: "Is Ouro fully live yet?",
        paragraphs: [
          <>
            Ouro is live, but <strong>very early</strong>. The platform is currently focused on
            building the founding player base region by region — so queues can open with enough
            density to feel like a real competitive environment, not a waiting room.
          </>,
        ],
        callout:
          "This is intentional. Ouro would rather grow slowly with the right players than open everything up and let queue quality collapse immediately. The founding period is the product.",
        link: { to: "/how-it-works", label: "How it works →" },
      },
      {
        id: "platform-queue",
        searchKeywords: "when can i queue regional evening windows timing schedule",
        question: "When can I actually queue?",
        paragraphs: [
          <>
            While the player base grows, queues are coordinated in{" "}
            <strong>regional evening windows</strong> — currently around 6–10pm local time. As a
            region reaches healthier density, those windows expand naturally toward more flexible
            matchmaking hours.
          </>,
          "What this means in practice: if you join now, you're helping your region reach the density that makes your own queue better. Early players have real leverage here.",
        ],
        link: { to: "/how-it-works", label: "See regional queue details →" },
      },
    ],
  },
  {
    id: "account",
    name: "Account & access",
    icon: (
      <>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </>
    ),
    items: [
      {
        id: "account-steam",
        searchKeywords: "steam sign in safe read only oauth inventory security",
        question: "Is Steam sign-in safe?",
        paragraphs: [
          <>
            Yes. Ouro uses official <strong>Steam OpenID</strong> for the main signup path — the
            same standard used by thousands of legitimate platforms. It is{" "}
            <strong>read-only</strong> by design.
          </>,
          "Ouro never asks for — and cannot access — your inventory, trades, market listings, API key, or Steam Guard codes. Google is available only as a fallback if Steam gives you trouble during signup.",
        ],
        callout:
          "What Ouro can see via Steam: your profile, game library, and public match history. That's it. Nothing that touches your items or account security.",
      },
      {
        id: "account-verify",
        searchKeywords: "id verification optional serious players throwaway accounts accountability",
        question: "Is ID verification required?",
        paragraphs: [
          <>
            No. A Steam account is all you need to join. <strong>ID checks are optional</strong> — if you
            choose one, it runs through didit.me and adds to your reputation. It is not required to sign
            up or play.
          </>,
          "What is required is the human review every signup goes through before the first match. Throwaway accounts, cheaters who cycle through bans, and repeat offenders who rely on fresh starts all become harder to maintain — that's what makes accountability on this platform meaningful rather than cosmetic.",
        ],
        link: { to: "/trust-and-safety", label: "How verification works →" },
      },
      {
        id: "account-region",
        searchKeywords: "my region not enough players demand early signup",
        question: "What if my region doesn't have enough players yet?",
        paragraphs: [
          <>
            Join anyway if you believe in what the platform is building.{" "}
            <strong>Early signups are how demand gets proven</strong> — and opening new server
            locations is straightforward once a region shows enough interest to justify it.
          </>,
          "The founding period is specifically designed for this: players who join early help make the platform better for themselves and for everyone who comes after them. Waiting for it to be \"ready\" means waiting for someone else to build it first.",
        ],
      },
    ],
  },
  {
    id: "competitive",
    name: "Competitive features",
    icon: <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />,
    items: [
      {
        id: "competitive-impact",
        searchKeywords: "impact rating what is it kills contribution score duels utility",
        question: "What is the Impact Rating?",
        paragraphs: [
          <>
            The Impact Rating is a single number that summarises{" "}
            <strong>how much you actually contributed in a match</strong> — not just kills. It looks
            at opening duels taken, trades executed, utility effectiveness, and whether you played for
            the round or for your own stat line.
          </>,
          <>
            Scores are centred around <strong>1.0</strong> — higher means a strong game, lower means a
            quiet one. You'll see it on match pages, lobby cards, your profile, and your full match
            history, with a short breakdown if you want to understand what drove the number.
          </>,
        ],
        callout:
          "It's deliberately not your KDA. A player who throws flashes, smokes sites, and plays for trades will rate higher than someone who baits for kills and goes 20-5.",
      },
      {
        id: "competitive-reputation",
        searchKeywords: "reputation hidden score understandable behavior reliability matches",
        question: "Is reputation just another hidden score?",
        paragraphs: [
          <>
            No. Reputation is designed to be <strong>understandable</strong> — not a black box. It
            reflects behavior and reliability across matches, broken down across six separate
            dimensions so you can see exactly what's contributing to your score and where.
          </>,
          "Other players can see your reputation breakdown when viewing your profile. Your detailed history and improvement tips stay private — visible only to you.",
        ],
        link: { to: "/reputation", label: "See the full reputation system →" },
      },
      {
        id: "competitive-women",
        searchKeywords: "women option opt-in soft preference team matchmaker quality of life",
        question: "Is there an option for women who want to play with other women?",
        paragraphs: [
          <>
            Yes. Women on Ouro can opt in to a <strong>soft preference</strong> that nudges the
            matchmaker toward putting them on a team with more women whenever it can do so without
            breaking team balance or slowing the queue.
          </>,
        ],
        callout:
          "It's opt-in, women only, and never a hard filter — so it doesn't change the experience for anyone who doesn't use it, and it never removes someone from a match. It's a small quality-of-life setting for a group that's consistently underserved in competitive CS, treated with the same respect as map and server preferences.",
      },
    ],
  },
  {
    id: "membership",
    name: "Memberships",
    icon: (
      <>
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
      </>
    ),
    items: [
      {
        id: "membership-free",
        searchKeywords: "free without paying access founding community no payment",
        question: "Can I use Ouro without paying?",
        paragraphs: [
          <>
            Yes. <strong>Free access</strong> gets you into the founding community, full
            matchmaking, reputation tracking, and Impact Rating — everything you need to actually play.
            No credit card, no trial period.
          </>,
          "Premium and Pro are for players who want deeper control over their queue — finer map and server filtering, priority placement, captain priority, and ELO recalibration. Useful when you know exactly how you want to play and want the tools to enforce it.",
        ],
        link: { to: "/pricing", label: "Compare all plans →" },
      },
      {
        id: "membership-when",
        searchKeywords: "when memberships matter control map server filter queue committed players",
        question: "When do memberships actually matter?",
        paragraphs: [
          <>
            Memberships matter when you want <strong>more deliberate control over your queue</strong>.
            Free access gives you the full matchmaking experience. Paid tiers let you narrow it — down
            to specific maps, specific server locations, and specific queue behaviors.
          </>,
          "If you don't care which map you play or where the server is, Free is fine indefinitely. If you grind one map or care deeply about ping, Premium pays for itself quickly. If you want priority placement and captain rights, Pro is for you.",
        ],
        link: { to: "/pricing", label: "See what each tier unlocks →" },
      },
    ],
  },
  {
    id: "anticheat",
    name: "Anti-cheat",
    icon: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
    items: [
      {
        id: "anticheat-faceit",
        searchKeywords: "faceit data import verification competitive baseline",
        question: "Does Ouro use FaceIT data?",
        paragraphs: [
          <>
            Yes. Ouro can <strong>import FaceIT information during verification</strong> so your
            account starts from a more relevant competitive baseline — rather than beginning at a
            blank slate with no context about who you are as a player.
          </>,
          "This is optional but recommended. The more context your profile has going in, the more accurate your initial reputation baseline and matchmaking placement will be.",
        ],
      },
      {
        id: "anticheat-cheaters",
        searchKeywords: "how does ouro handle cheaters server-side anti-cheat tick detection bans",
        question: "How does Ouro handle cheaters?",
        paragraphs: [
          <>
            Ouro runs a <strong>server-side anti-cheat</strong> that collects tick-by-tick gameplay
            data during live matches and flags suspicious behavior — prefire angle consistency, wallhit
            timing, trigger speed, kill chain patterns, and reaction times that don't look human.
          </>,
          <>
            Server-side means the analysis happens on the match server, not on your machine. It{" "}
            <strong>cannot be tampered with from the client side</strong> — and is structurally immune
            to DMA hardware cheats that defeat kernel-level drivers entirely.
          </>,
          "Combined with account verification and reputation tracking, bans are meaningful and hard to evade — not something you can walk away from by creating a new account.",
        ],
        link: { to: "/anti-cheat", label: "See the full anti-cheat breakdown →" },
      },
    ],
  },
];

function Eyebrow({ children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: 2 }}>
      <Box sx={{ width: 24, height: "1px", background: colors.primary.hover }} />
      <Typography
        sx={{
          fontSize: { xs: "0.7rem", md: "0.75rem" },
          fontWeight: 600,
          letterSpacing: "0.12em",
          color: colors.primary.hover,
          textTransform: "uppercase",
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function HeroAurora() {
  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        zIndex: 0,
        "@keyframes auroradrift1": {
          "0%": { transform: "translate(0%, 0%) scale(1)" },
          "33%": { transform: "translate(6%, 10%) scale(1.1)" },
          "66%": { transform: "translate(-5%, 6%) scale(0.93)" },
          "100%": { transform: "translate(0%, 0%) scale(1)" },
        },
        "@keyframes auroradrift2": {
          "0%": { transform: "translate(0%, 0%) scale(1)" },
          "40%": { transform: "translate(-8%, -6%) scale(1.08)" },
          "70%": { transform: "translate(5%, -10%) scale(0.96)" },
          "100%": { transform: "translate(0%, 0%) scale(1)" },
        },
        "@keyframes auroradrift3": {
          "0%": { transform: "translate(0%, 0%) scale(1)" },
          "50%": { transform: "translate(10%, 5%) scale(1.06)" },
          "100%": { transform: "translate(0%, 0%) scale(1)" },
        },
      }}
    >
      {[
        {
          animation: "auroradrift1 22s ease-in-out infinite",
          background:
            "radial-gradient(ellipse at 85% 45%, rgba(232,163,61,0.1) 0%, transparent 60%), " +
            "radial-gradient(ellipse at 5% 70%, rgba(232,163,61,0.05) 0%, transparent 55%)",
          width: "140%",
          height: "320px",
          top: "-120px",
          left: "-20%",
          filter: "blur(48px)",
        },
        {
          animation: "auroradrift2 28s ease-in-out infinite",
          background:
            "radial-gradient(ellipse at 50% 100%, rgba(220,38,38,0.04) 0%, transparent 50%), " +
            "radial-gradient(ellipse at 60% 40%, rgba(246,197,111,0.08) 0%, transparent 60%)",
          width: "120%",
          height: "260px",
          top: "-80px",
          left: "-10%",
          filter: "blur(56px)",
        },
        {
          animation: "auroradrift3 34s ease-in-out infinite",
          background: "radial-gradient(ellipse at 50% 30%, rgba(255,216,154,0.06) 0%, transparent 55%)",
          width: "100%",
          height: "200px",
          top: "-50px",
          left: "0%",
          filter: "blur(60px)",
        },
      ].map((style, i) => (
        <Box key={i} sx={{ position: "absolute", ...style }} />
      ))}
    </Box>
  );
}

function AccordionLink({ to, label }) {
  return (
    <Box
      component={Link}
      to={to}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        mt: 2,
        fontSize: "0.6875rem",
        fontWeight: 600,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: colors.primary.main,
        textDecoration: "none",
        transition: "opacity .2s",
        "&:hover": { opacity: 0.75 },
      }}
    >
      {label}
    </Box>
  );
}

function FaqAccordionItem({ item, open, onToggle }) {
  return (
    <Box
      sx={{
        background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
        border: `1px solid ${open ? "rgba(232,163,61,0.25)" : colors.border.subtle}`,
        overflow: "hidden",
        transition: "border-color .3s",
        "&:hover": { borderColor: open ? "rgba(232,163,61,0.25)" : "rgba(255,255,255,0.08)" },
      }}
    >
      <Box
        component="button"
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: { xs: "20px 22px", md: "24px 28px" },
          cursor: "pointer",
          background: open ? "rgba(232,163,61,0.03)" : "transparent",
          border: "none",
          textAlign: "left",
          transition: "background .2s",
          "&:hover": { background: "rgba(255,255,255,0.015)" },
        }}
      >
        <Typography
          sx={{
            flex: 1,
            fontFamily: "'Inter', system-ui, sans-serif",
            fontSize: { xs: "0.9375rem", md: "1.0625rem" },
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: open ? colors.text.primary : colors.text.secondary,
            lineHeight: 1.2,
            transition: "color .2s",
          }}
        >
          {item.question}
        </Typography>
        <Box
          sx={{
            width: 20,
            height: 20,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: `1px solid ${open ? "rgba(232,163,61,0.25)" : colors.border.subtle}`,
            background: open ? "rgba(232,163,61,0.12)" : "transparent",
            transform: open ? "rotate(45deg)" : "none",
            transition: "transform .35s cubic-bezier(.22,1,.36,1), border-color .2s, background .2s",
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 10 10"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            sx={{ width: 10, height: 10, color: open ? colors.primary.main : colors.text.muted }}
          >
            <line x1="5" y1="1" x2="5" y2="9" />
            <line x1="1" y1="5" x2="9" y2="5" />
          </Box>
        </Box>
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: { xs: "22px", md: "28px" }, pb: { xs: "22px", md: "28px" }, borderTop: `1px solid ${colors.border.subtle}` }}>
          <Box sx={{ pt: 2.5 }}>
            {item.paragraphs.map((paragraph, index) => (
              <Typography
                key={index}
                component="div"
                sx={{
                  fontSize: "0.9375rem",
                  color: colors.text.secondary,
                  lineHeight: 1.8,
                  mt: index > 0 ? 1.5 : 0,
                  "& strong": { color: colors.text.primary, fontWeight: 600 },
                }}
              >
                {paragraph}
              </Typography>
            ))}
            {item.callout ? (
              <Box
                sx={{
                  mt: 2,
                  p: "16px 20px",
                  background: "linear-gradient(180deg, rgba(232,163,61,0.04), transparent)",
                  border: "1px solid rgba(232,163,61,0.25)",
                  borderLeft: `2px solid ${colors.primary.main}`,
                }}
              >
                <Typography sx={{ fontSize: "0.8125rem", color: colors.text.secondary, lineHeight: 1.7 }}>
                  {item.callout}
                </Typography>
              </Box>
            ) : null}
            {item.link ? <AccordionLink to={item.link.to} label={item.link.label} /> : null}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

function itemMatchesSearch(item, query) {
  if (!query) return true;
  const haystack = `${item.searchKeywords} ${item.question}`.toLowerCase();
  return haystack.includes(query);
}

export default function FaqPage() {
  const [activeCategory, setActiveCategory] = React.useState("all");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [openItemId, setOpenItemId] = React.useState("platform-live");

  const normalizedSearch = searchQuery.trim().toLowerCase();

  const visibleByCategory = React.useMemo(() => {
    const result = {};
    FAQ_CATEGORIES.forEach((category) => {
      result[category.id] = category.items.filter((item) => {
        const categoryMatch = activeCategory === "all" || activeCategory === category.id;
        return categoryMatch && itemMatchesSearch(item, normalizedSearch);
      });
    });
    return result;
  }, [activeCategory, normalizedSearch]);

  const visibleCount = React.useMemo(
    () => Object.values(visibleByCategory).reduce((sum, items) => sum + items.length, 0),
    [visibleByCategory]
  );

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setOpenItemId(null);
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
    if (value.trim()) {
      setActiveCategory("all");
    }
  };

  const toggleItem = (itemId) => {
    setOpenItemId((current) => (current === itemId ? null : itemId));
  };

  const searchHint =
    normalizedSearch.length > 0
      ? `${visibleCount} result${visibleCount === 1 ? "" : "s"}`
      : `${TOTAL_QUESTIONS} questions`;

  return (
    <Box sx={{ backgroundColor: HERO_BG, color: colors.text.primary, overflowX: "hidden" }}>
      <Box component="header" sx={heroSectionSx}>
        <HeroAurora />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            opacity: 0.5,
            backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(232,163,61,0.02) 3px, rgba(232,163,61,0.02) 4px)`,
            "@keyframes scanMove": {
              from: { backgroundPosition: "0 0" },
              to: { backgroundPosition: "0 400px" },
            },
            animation: "scanMove 8s linear infinite",
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            backgroundImage: `
              linear-gradient(${colors.border.subtle} 1px, transparent 1px),
              linear-gradient(90deg, ${colors.border.subtle} 1px, transparent 1px)
            `,
            backgroundSize: "80px 80px",
            maskImage: "radial-gradient(ellipse 90% 70% at 50% 0%, black 0%, transparent 70%)",
          }}
        />
        <Box sx={{ position: "relative", zIndex: 2, maxWidth: "75rem", mx: "auto", width: "100%" }}>
          <MotionBox {...HERO_ENTER} transition={REVEAL_TRANSITION}>
            <Eyebrow>Frequently asked</Eyebrow>
          </MotionBox>

          <MotionBox
            component="h1"
            {...HERO_ENTER}
            transition={{ ...REVEAL_TRANSITION, delay: 0.1 }}
            sx={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: { xs: "2.25rem", sm: "2.5rem", md: "3.25rem", lg: "3.5rem", xl: "3.75rem" },
              fontWeight: 700,
              letterSpacing: "-0.075em",
              textTransform: "uppercase",
              lineHeight: 1,
              color: colors.text.primary,
              maxWidth: 860,
              mb: 4,
            }}
          >
            Questions players ask
            <br />
            before they{" "}
            <Box
              component="span"
              sx={{
                background: GOLD_GRADIENT,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              commit.
            </Box>
          </MotionBox>

          <MotionBox {...HERO_ENTER} transition={{ ...REVEAL_TRANSITION, delay: 0.2 }}>
            <Typography
              sx={{
                fontSize: { xs: "0.875rem", md: "1rem" },
                fontWeight: 500,
                lineHeight: 1.75,
                color: "rgba(255,255,255,0.72)",
                maxWidth: "32rem",
                mb: 5,
              }}
            >
              Everything about verification, reputation, memberships, and how Ouro approaches
              competitive matchmaking. No vague answers.
            </Typography>
          </MotionBox>

          <MotionBox
            {...HERO_ENTER}
            transition={{ ...REVEAL_TRANSITION, delay: 0.3 }}
            sx={{
              display: "inline-flex",
              border: `1px solid ${colors.border.subtle}`,
              flexWrap: "wrap",
            }}
          >
            {[
              { num: "12", label: "Questions" },
              { num: "5", label: "Topics" },
              { num: "0", label: "Non-answers" },
            ].map((stat, index) => (
              <Box
                key={stat.label}
                sx={{
                  px: { xs: "24px", md: "32px" },
                  py: 2,
                  borderRight: index < 2 ? `1px solid ${colors.border.subtle}` : "none",
                  borderBottom: { xs: index < 2 ? `1px solid ${colors.border.subtle}` : "none", sm: "none" },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: "1.75rem",
                    fontWeight: 700,
                    lineHeight: 1,
                    color: colors.text.primary,
                    mb: 0.5,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      background: GOLD_GRADIENT,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {stat.num}
                  </Box>
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.5625rem",
                    fontWeight: 700,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    color: colors.text.muted,
                  }}
                >
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </MotionBox>
        </Box>
      </Box>

      <PageContainer sx={{ maxWidth: "75rem", pt: { xs: 4, md: 6 }, pb: 0 }}>
        <MotionBox
          {...REVEAL}
          transition={REVEAL_TRANSITION}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.75,
            px: { xs: 2, md: 3 },
            py: 2,
            background: colors.background.card,
            border: `1px solid rgba(255,255,255,0.08)`,
            transition: "border-color .3s",
            "&:focus-within": { borderColor: "rgba(232,163,61,0.25)" },
          }}
        >
          <SearchRounded sx={{ color: colors.text.muted, fontSize: 18 }} />
          <InputBase
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search questions — try 'cheat', 'reputation', 'free'…"
            inputProps={{ "aria-label": "Search FAQ" }}
            sx={{
              flex: 1,
              fontSize: "0.9375rem",
              color: colors.text.primary,
              "& input::placeholder": { color: colors.text.muted, opacity: 1 },
            }}
          />
          <Typography
            sx={{
              fontSize: "0.625rem",
              fontWeight: 700,
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: colors.text.muted,
              flexShrink: 0,
              display: { xs: "none", sm: "block" },
            }}
          >
            {searchHint}
          </Typography>
        </MotionBox>
      </PageContainer>

      <PageContainer
        sx={{
          maxWidth: "75rem",
          py: { xs: 5, md: 9 },
          pb: { xs: 10, md: 16 },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "260px 1fr" },
          gap: { xs: 5, md: 10 },
          alignItems: "start",
        }}
      >
        <MotionBox
          component="aside"
          {...REVEAL}
          transition={REVEAL_TRANSITION}
          sx={{
            position: { md: "sticky" },
            top: { md: 88 },
          }}
        >
          <Typography
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: 1,
              fontSize: "0.5625rem",
              fontWeight: 700,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: colors.text.muted,
              mb: 2.5,
              "&::before": {
                content: '""',
                width: 14,
                height: "1px",
                background: colors.text.muted,
                display: "block",
              },
            }}
          >
            Browse by topic
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "row", md: "column" },
              flexWrap: { xs: "wrap", md: "nowrap" },
              gap: { xs: 1, md: "2px" },
            }}
          >
            {NAV_CATEGORIES.map((cat) => {
              const active = activeCategory === cat.id;
              return (
                <Box
                  key={cat.id}
                  component="button"
                  type="button"
                  onClick={() => handleCategoryChange(cat.id)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    width: { xs: "auto", md: "100%" },
                    p: { xs: "8px 14px", md: "12px 16px" },
                    cursor: "pointer",
                    background: active
                      ? "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)"
                      : "transparent",
                    border: `1px solid ${active ? colors.border.subtle : "transparent"}`,
                    textAlign: "left",
                    position: "relative",
                    color: active ? colors.text.primary : colors.text.muted,
                    transition: "color .2s, background .2s, border-color .2s",
                    "&:hover": { color: colors.text.secondary },
                    "&::before": active
                      ? {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: "2px",
                          background: colors.primary.main,
                          display: { xs: "none", md: "block" },
                        }
                      : {},
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      flexShrink: 0,
                      background: active ? colors.primary.main : colors.background.surface,
                      display: { xs: "none", md: "block" },
                    }}
                  />
                  <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500 }}>{cat.label}</Typography>
                  <Typography
                    sx={{
                      ml: { md: "auto" },
                      fontSize: "0.625rem",
                      fontWeight: 700,
                      letterSpacing: "0.1em",
                      color: active ? colors.primary.main : colors.text.muted,
                    }}
                  >
                    {cat.count}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Box
            sx={{
              display: { xs: "none", md: "block" },
              mt: 3,
              pt: 3,
              borderTop: `1px solid ${colors.border.subtle}`,
            }}
          >
            <Box
              sx={{
                p: 2,
                background: "linear-gradient(180deg, rgba(232,163,61,0.04), transparent)",
                border: "1px solid rgba(232,163,61,0.25)",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.5625rem",
                  fontWeight: 700,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: colors.primary.main,
                  mb: 1,
                }}
              >
                Still have questions?
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: colors.text.secondary, lineHeight: 1.6, mb: 1.5 }}>
                Can&apos;t find what you&apos;re looking for? Open a support ticket and the team will get back to you.
              </Typography>
              <Box
                component={Link}
                to="/support"
                sx={{
                  fontSize: "0.625rem",
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: colors.primary.main,
                  textDecoration: "none",
                  "&:hover": { opacity: 0.75 },
                }}
              >
                Open a ticket →
              </Box>
            </Box>
          </Box>
        </MotionBox>

        <MotionBox component="main" {...REVEAL} transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}>
          {FAQ_CATEGORIES.map((category) => {
            const visibleItems = visibleByCategory[category.id];
            if (!visibleItems.length) return null;

            return (
              <Box key={category.id} sx={{ mb: 10, "&:last-of-type": { mb: 0 } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 4,
                    pb: 2.5,
                    borderBottom: `1px solid ${colors.border.subtle}`,
                  }}
                >
                  <Box
                    component="svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    sx={{ width: 20, height: 20, color: colors.primary.main, flexShrink: 0 }}
                  >
                    {category.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "'Inter', system-ui, sans-serif",
                      fontSize: "1.625rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      color: colors.text.primary,
                    }}
                  >
                    {category.name}
                  </Typography>
                  <Typography
                    sx={{
                      ml: "auto",
                      fontSize: "0.5625rem",
                      fontWeight: 700,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      color: colors.primary.main,
                      background: "rgba(232,163,61,0.12)",
                      border: "1px solid rgba(232,163,61,0.2)",
                      px: 1.25,
                      py: 0.5,
                    }}
                  >
                    {visibleItems.length} question{visibleItems.length === 1 ? "" : "s"}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                  {visibleItems.map((item) => (
                    <FaqAccordionItem
                      key={item.id}
                      item={item}
                      open={openItemId === item.id}
                      onToggle={() => toggleItem(item.id)}
                    />
                  ))}
                </Box>
              </Box>
            );
          })}

          {visibleCount === 0 ? (
            <Box
              sx={{
                p: 6,
                textAlign: "center",
                border: `1px solid ${colors.border.subtle}`,
              }}
            >
              <Typography sx={{ fontSize: "0.875rem", color: colors.text.muted }}>
                No questions match that search. Try a different term — or{" "}
                <Box
                  component={Link}
                  to="/support"
                  sx={{ color: colors.primary.main, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                >
                  open a support ticket
                </Box>{" "}
                if you need a direct answer.
              </Typography>
            </Box>
          ) : null}
        </MotionBox>
      </PageContainer>

      <Box component="section" sx={sectionSx}>
        <PageContainer
          sx={{
            maxWidth: "75rem",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
            alignItems: "center",
            gap: { xs: 5, md: 8 },
          }}
        >
          <MotionBox {...REVEAL} transition={REVEAL_TRANSITION}>
            <Typography component="h2" sx={{ ...titleSx, mb: 2 }}>
              Still not sure?
              <br />
              Start free
              <br />
              and see.
            </Typography>
            <Typography sx={{ fontSize: "0.95rem", color: colors.text.secondary, maxWidth: 400, lineHeight: 1.7 }}>
              No credit card. No commitment beyond verification. Join the founding community and see how it plays
              before you decide anything else.
            </Typography>
          </MotionBox>
          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              alignItems: { xs: "flex-start", md: "flex-end" },
            }}
          >
            <Box component={Link} to={{ pathname: "/" }} sx={btnPrimarySx}>
              Join for free
            </Box>
            <Box component={Link} to="/how-it-works" sx={btnGhostSx}>
              How it works →
            </Box>
          </MotionBox>
        </PageContainer>
      </Box>
    </Box>
  );
}
